import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingCart,
  Boxes,
  ReceiptText,
  Wallet,
  Landmark,
  Bell,
  Sparkles,
  BarChart3,
  Users,
  Building2,
  Settings as SettingsIcon,
  CircleDot,
  CreditCard,
  LifeBuoy,
  Share2,
  FileText,
  WalletCards,
  Coins,
  GraduationCap,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Logo } from "@/components/brand/Logo";
import { useAuth, useWorkspace } from "@/lib/store";
import { formatSessionRole } from "@/lib/sessionLabels";
import { useLocalization } from "@/lib/localization";
import { hasPermission } from "@/lib/permissions";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

const NAV_PRIMARY = [
  { to: "/dashboard",     labelKey: "dashboard",     icon: LayoutDashboard, perm: "analytics",  testid: "nav-dashboard" },
  { to: "/sell",          labelKey: "sell",          icon: ShoppingCart,    perm: "sell",       testid: "nav-sell" },
  { to: "/inventory",     labelKey: "inventory",     icon: Boxes,           perm: "inventory",  testid: "nav-inventory" },
  { to: "/history",       labelKey: "history",       icon: ReceiptText,     perm: "history",    testid: "nav-history" },
  { to: "/debts",         labelKey: "debts",         icon: Wallet,          perm: "debts",      testid: "nav-debts" },
  { to: "/expenses",      labelKey: "expenses",      icon: Landmark,        perm: "expenses",   testid: "nav-expenses" },
  { to: "/analytics",     labelKey: "analytics",     icon: BarChart3,       perm: "analytics",  testid: "nav-analytics" },
];

const NAV_SECONDARY = [
  { to: "/tutorials",     labelKey: "tutorials",     labelFallback: "Tutorials", icon: GraduationCap, perm: null, testid: "nav-tutorials" },
  { to: "/referrals",     labelKey: "referrals",     icon: Share2,          perm: null, role: "Owner", testid: "nav-referrals" },
  { to: "/subscription",  labelKey: "subscription",  icon: CreditCard,      perm: "billing",               testid: "nav-subscription" },
  { to: "/assistant",     labelKey: "assistant",     icon: Sparkles,        perm: "assistant",        testid: "nav-assistant", badge: "AI" },
  { to: "/notifications", labelKey: "notifications", icon: Bell,            perm: "notifications",    testid: "nav-notifications" },
  { to: "/staff",         labelKey: "staff",         icon: Users,           perm: "staff_access",     testid: "nav-staff" },
  { to: "/branches",      labelKey: "branches",      icon: Building2,       perm: "branch_management",testid: "nav-branches" },
  { to: "/reports",       labelKey: "reports",       icon: FileText,        perm: "analytics",         testid: "nav-reports" },
  { to: "/wallet",        labelKey: "wallet",        icon: WalletCards,     perm: "billing", testid: "nav-wallet" },
  { to: "/credits",       labelKey: "credits",       icon: Coins,           perm: "billing", testid: "nav-credits" },
  { to: "/settings",      labelKey: "settings",      icon: SettingsIcon,    perm: "settings",         testid: "nav-settings" },
];

export default function Sidebar({ mobileOpen = false, onMobileOpenChange }) {
  const { session } = useAuth();
  const { currentBranch } = useWorkspace();
  const { isRTL, messages } = useLocalization();
  const copy = messages.layout;
  const location = useLocation();

  const has = (perm) => hasPermission(session, perm);

  const content = (onNavigate) => (
    <>
      {/* Ambient light glow at top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-[hsl(178,70%,45%)]/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-64 h-64 rounded-full bg-[hsl(38,85%,55%)]/[0.06] blur-3xl pointer-events-none" />

      {/* Brand */}
      <div className="relative h-16 px-5 flex items-center border-b border-sidebar-border/60">
        <div className="flex items-center gap-2.5">
          <Logo size={28} wordmark={false} className="shrink-0" />
          <div className="leading-none">
            <div className="text-[15px] font-bold tracking-[-0.01em] text-sidebar-foreground">Shopbook</div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-sidebar-foreground/55 mt-1">{copy.retailOperations}</div>
          </div>
        </div>
      </div>

      {/* Independently scrollable navigation; brand and account remain anchored. */}
      <div className="relative min-h-0 flex-1">
        <nav
          aria-label={copy.workspace}
          className="sidebar-scrollbar h-full min-h-0 space-y-6 overflow-y-auto overscroll-contain px-3 py-5"
        >
          <NavGroup title={copy.workspace}>
            {NAV_PRIMARY.filter((n) => has(n.perm)).map((n) => (
              <NavItem key={n.to} {...n} onClick={onNavigate} label={copy[n.labelKey] || n.labelFallback} active={location.pathname === n.to || location.pathname.startsWith(`${n.to}/`)} />
            ))}
          </NavGroup>

          <NavGroup title={copy.operate}>
            {NAV_SECONDARY.filter((n) => has(n.perm) && (!n.role || session?.role === n.role)).map((n) => (
              <NavItem key={n.to} {...n} onClick={onNavigate} label={copy[n.labelKey]} active={location.pathname === n.to || location.pathname.startsWith(`${n.to}/`)} />
            ))}
          </NavGroup>
        </nav>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-3 bg-gradient-to-b from-sidebar to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-5 bg-gradient-to-t from-sidebar to-transparent" />
      </div>

      {/* Footer — current branch + role */}
      <div className="relative border-t border-sidebar-border/60 p-3">
        <NavLink
          to="/support"
          onClick={onNavigate}
          data-testid="nav-support"
          className={({ isActive }) => `mb-2 flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-medium transition-all ${
            isActive
              ? "bg-[hsl(178,60%,50%)]/20 text-white ring-1 ring-[hsl(178,70%,70%)]/20"
              : "text-sidebar-foreground/75 hover:bg-white/[0.07] hover:text-white"
          }`}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.08]">
            <LifeBuoy className="h-4 w-4" />
          </span>
          <span className="flex-1">
            <span className="block">{copy.support || "Help & support"}</span>
            <span className="block text-[10px] font-normal text-sidebar-foreground/50">
              {copy.supportMeta || "Cases & conversations"}
            </span>
          </span>
        </NavLink>
        <div className="rounded-xl bg-[hsl(0,0%,100%)]/[0.06] ring-1 ring-[hsl(0,0%,100%)]/10 p-2.5 flex items-center gap-3 backdrop-blur-sm">
          <Avatar className="h-8 w-8 ring-2 ring-[hsl(178,70%,60%)]/40">
            <AvatarFallback className="bg-gradient-to-br from-[hsl(178,72%,42%)] to-[hsl(178,80%,26%)] text-white font-semibold text-sm">
              {session?.shopName?.slice(0, 2)?.toUpperCase() ?? "SB"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold truncate tracking-[-0.01em] text-sidebar-foreground">{session?.shopName}</div>
            <div className="text-[11px] text-sidebar-foreground/60 truncate inline-flex items-center gap-1 mt-0.5">
              <CircleDot className="h-2 w-2 text-[hsl(158,70%,55%)]" />
              {currentBranch?.name} · {formatSessionRole(session?.role)}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <aside className={`hidden lg:flex fixed inset-y-0 z-30 w-[275px] max-w-[275px] min-[1050px]:max-[1300px]:w-[250px] min-[1050px]:max-[1300px]:max-w-[250px] flex-col bg-sidebar text-sidebar-foreground overflow-hidden ${
        isRTL ? "right-0 border-l border-sidebar-border" : "left-0 border-r border-sidebar-border"
      }`}>
        {content()}
      </aside>

      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent
          id="mobile-workspace-navigation"
          side={isRTL ? "right" : "left"}
          className="flex w-[min(86vw,320px)] max-w-[320px] flex-col gap-0 overflow-hidden border-sidebar-border bg-sidebar p-0 text-sidebar-foreground shadow-[0_28px_80px_-24px_rgba(0,0,0,0.65)] lg:hidden [&>button]:right-5 [&>button]:top-5 [&>button]:z-10 [&>button]:text-sidebar-foreground [&>button]:ring-offset-sidebar"
        >
          <SheetTitle className="sr-only">{copy.workspace}</SheetTitle>
          {content(() => onMobileOpenChange?.(false))}
        </SheetContent>
      </Sheet>
    </>
  );
}

function NavGroup({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-sidebar-foreground/45 px-2 mb-2.5">
        {title}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, active, badge, testid, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={testid}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-200
        ${isActive || active
          ? "bg-[hsl(0,0%,100%)]/[0.14] text-white ring-1 ring-[hsl(0,0%,100%)]/10 shadow-[0_1px_0_hsl(0,0%,100%)/0.06_inset]"
          : "text-sidebar-foreground/70 hover:bg-[hsl(0,0%,100%)]/[0.06] hover:text-white"
        }`
      }
    >
      <Icon className="h-[17px] w-[17px] shrink-0" strokeWidth={2} />
      <span className="flex-1 truncate">{label}</span>
      {badge && (
        <span className="text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-[hsl(38,90%,60%)]/25 text-[hsl(38,95%,80%)]">
          {badge}
        </span>
      )}
    </NavLink>
  );
}
